import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Grid,
    Table,
    CircularProgress,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Typography,
    TablePagination,
    Chip,
    FormControlLabel,
    Switch,
    Snackbar,
    Alert
} from '@mui/material';
import { Delete, Edit, Add, FilterAltOff } from '@mui/icons-material';
import { languageSupportAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';

const LanguageSupport = () => {
    const { t } = useLanguage();
    const adminUser = getAdminUser();
    const isSuperAdmin = /super\s*admin/i.test(adminUser?.role || '');

    const [isLoading, setIsLoading] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [languages, setLanguages] = useState([]);
    const [toastOpen, setToastOpen] = useState(false);
    const [toast, setToast] = useState({ text: '', type: 'info' });
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [open, setOpen] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedNameSearch, setDebouncedNameSearch] = useState('');
    const debouncedNameSearchRef = useRef(debouncedNameSearch);
    useEffect(() => {
        debouncedNameSearchRef.current = debouncedNameSearch;
    }, [debouncedNameSearch]);

    useEffect(() => {
        const id = window.setTimeout(() => {
            const next = (searchQuery || '').trim();
            const prev = debouncedNameSearchRef.current;
            if (prev !== next) {
                setPage(0);
            }
            setDebouncedNameSearch(next);
        }, 400);
        return () => window.clearTimeout(id);
    }, [searchQuery]);

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        languageCode: '',
        isActive: true,
    });
    const showToast = (text, type = 'info') => {
        setToast({ text, type });
        setToastOpen(true);
    };

    const fetchLanguages = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await languageSupportAPI.getLanguages(page, rowsPerPage, {
                name: debouncedNameSearch,
            });
            if (response?.data?.success === true && Array.isArray(response.data.payload)) {
                const list = response.data.payload;
                setLanguages(list);
                const apiTotal = response.data.totalRecord;
                setTotalCount(
                    apiTotal != null && Number(apiTotal) >= 0 ? Number(apiTotal) : list.length,
                );
            } else {
                setLanguages([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching languages:', error);
            setLanguages([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, debouncedNameSearch]);

    useEffect(() => {
        fetchLanguages();
    }, [fetchLanguages]);

    const handleOpen = (language = null) => {
        if (language) {
            setSelectedLanguage(language);
            setFormData({
                id: language.id || language.languageSupportId || '',
                name: language.name || '',
                languageCode: language.languageCode || '',
                isActive: language.isActive !== undefined ? language.isActive : true,
            });
        } else {
            setSelectedLanguage(null);
            setFormData({
                id: '',
                name: '',
                languageCode: '',
                isActive: true,
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedLanguage(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const buildPayload = () => {
        return {
            id: selectedLanguage ? (selectedLanguage.id || selectedLanguage.languageSupportId || 0) : 0,
            name: formData.name || '',
            languageCode: formData.languageCode || '',
            isActive: Boolean(formData.isActive),
        };
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const payload = buildPayload();
            if (selectedLanguage) {
                await languageSupportAPI.updateLanguage(payload);
                showToast(t('langSupport.toastUpdated'), 'success');
            } else {
                await languageSupportAPI.createLanguage(payload);
                showToast(t('langSupport.toastCreated'), 'success');
            }
            fetchLanguages();
            handleClose();
        } catch (error) {
            console.error('Error saving language support:', error);
            showToast(error?.response?.data?.errorMessage || t('langSupport.toastSaveError'), 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('langSupport.confirmDelete'))) {
            setIsLoading(true);
            try {
                await languageSupportAPI.deleteLanguage(id);
                showToast(t('langSupport.toastDeleted'), 'success');
                fetchLanguages();
            } catch (error) {
                console.error('Error deleting language support:', error);
                showToast(error?.response?.data?.errorMessage || t('langSupport.toastDeleteError'), 'error');
            } finally {
                setIsLoading(false);
            }
        }
    };

    /** Display filter: language display name only (not title, not code). */
    const filteredLanguages = useMemo(() => {
        const q = debouncedNameSearch.trim().toLowerCase();
        if (!q) return languages;
        const hay = (v) => String(v ?? '').toLowerCase();
        return languages.filter(
            (lang) => hay(lang.name).includes(q) || hay(lang.Name).includes(q),
        );
    }, [languages, debouncedNameSearch]);

    return (
        <Box p={1}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                mb={2}
                flexDirection={{ xs: 'column', sm: 'row' }}
                flexWrap="wrap"
                gap={1}
            >
                <Typography variant="h4" color="primary">
                    {t('langSupport.title')}
                </Typography>
                {isSuperAdmin && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpen()}
                        sx={{
                            background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
                            color: '#ffffff',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
                            },
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        {t('langSupport.add')}
                    </Button>
                )}
            </Box>
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: '#fff',
                    borderRadius: 1,
                    boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            size="small"
                            fullWidth
                            label={t('common.name')}
                            placeholder={t('langSupport.searchPh')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: '#0F3D3E',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#0F3D3E',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md="auto"
                        sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FilterAltOff />}
                            onClick={() => {
                                setSearchQuery('');
                                setDebouncedNameSearch('');
                            }}
                            sx={clearFilterButtonSx}
                        >
                            {t('common.clear')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress sx={{ color: '#0F3D3E' }} />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
                        borderRadius: 2,
                        '& .MuiTableCell-head': {
                            backgroundColor: '#ebf4f4',
                            color: '#0F3D3E',
                            fontWeight: 600,
                            padding: '16px'
                        },
                        '& .MuiTableCell-body': {
                            padding: '16px'
                        }
                    }}
                >
                    <Table sx={{ minWidth: 700 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('common.id')}</TableCell>
                                <TableCell>{t('common.name')}</TableCell>
                                <TableCell>{t('langSupport.colLangCode')}</TableCell>
                                <TableCell>{t('common.status')}</TableCell>
                                <TableCell>{t('common.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLanguages.map((lang) => (
                                <TableRow
                                    key={lang.id || lang.languageSupportId}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: '#ebf4f4',
                                        }
                                    }}
                                >
                                    <TableCell>{lang.id || lang.languageSupportId}</TableCell>
                                    <TableCell>{lang.name}</TableCell>
                                    <TableCell>{lang.languageCode}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={lang.isActive ? t('common.active') : t('common.inactive')}
                                            color={lang.isActive ? 'success' : 'error'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => handleOpen(lang)}
                                            sx={{ color: '#0F3D3E' }}
                                        >
                                            <Edit />
                                        </IconButton>
                                        {isSuperAdmin && (
                                            <IconButton
                                                onClick={() => handleDelete(lang.id || lang.languageSupportId)}
                                                sx={{ color: '#ef4444' }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredLanguages.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 4 }}>
                                        {t('langSupport.empty')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage={t('common.rowsPerPage')}
                        sx={{
                            '.MuiTablePagination-select': { color: '#0F3D3E' },
                            '.MuiTablePagination-selectIcon': { color: '#0F3D3E' },
                            '.MuiTablePagination-displayedRows': { color: '#0F3D3E' },
                            '.MuiTablePagination-actions': {
                                '& .MuiIconButton-root': { color: '#0F3D3E' },
                                '& .Mui-disabled': { color: '#e9d5ff' },
                            }
                        }}
                    />
                </TableContainer>
            )}

            {/* Add / Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedLanguage ? t('langSupport.dialogEdit') : t('langSupport.dialogAdd')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                name="name"
                                label={t('common.name')}
                                fullWidth
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                name="languageCode"
                                label={t('langSupport.fieldLangCode')}
                                fullWidth
                                placeholder={t('langSupport.fieldLangCodePh')}
                                value={formData.languageCode}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(formData.isActive)}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                        color="success"
                                    />
                                }
                                label={t('langSupport.switchActive')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={isSubmitting}>{t('common.cancel')}</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
                        sx={{
                            background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
                            }
                        }}
                    >
                        {selectedLanguage ? t('common.update') : t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
                    {toast.text}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LanguageSupport;
